import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaCheck,
  FaClock,
  FaEnvelope,
  FaKey,
  FaMapMarkerAlt,
  FaPhone,
  FaShieldAlt,
  FaSignOutAlt,
  FaUser
} from 'react-icons/fa';
import { PopupContext } from '../App';
import { Badge, Button, Card, CardBody, CardHeader, Input, PageHeader } from '../shared/ui';
import Modal from '../components/Modal';
import { getCurrentUser, updateCurrentUser } from '../services/userService';
import { resetPassword } from '../services/passwordService';
import { logout } from '../services/authService';
import styles from './ProfilePage.module.css';

const PASSWORD_RULES = [
  'At least 8 characters',
  'At least one uppercase letter',
  'At least one lowercase letter',
  'At least one number'
];

function getInitials(user) {
  const source = user?.full_name || user?.name || user?.email || '';
  if (!source) return '?';
  const parts = source.replace(/@.*$/, '').split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return source.charAt(0).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatDateTime(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function isValidPassword(p) {
  return p.length >= 8 && /[A-Z]/.test(p) && /[a-z]/.test(p) && /[0-9]/.test(p);
}

const DEFAULT_PROFILE = {
  full_name: '',
  email: '',
  phone: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  postal_code: '',
  country: 'United States'
};

export default function ProfilePage({ user, setUser }) {
  const showPopup = useContext(PopupContext);
  const navigate = useNavigate();

  const [profile, setProfile] = useState(DEFAULT_PROFILE);
  const [savingContact, setSavingContact] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (!user) {
      getCurrentUser()
        .then((u) => setUser && setUser(u))
        .catch(() => {});
    }
  }, [user, setUser]);

  useEffect(() => {
    if (!user) return;
    setProfile((prev) => ({
      ...DEFAULT_PROFILE,
      ...prev,
      ...Object.fromEntries(
        Object.entries(user).filter(([key]) => key in DEFAULT_PROFILE && user[key] != null)
      )
    }));
  }, [user]);

  const initials = useMemo(() => getInitials(user), [user]);
  const displayName = user?.full_name || 'New User';
  const role = user?.role || 'User';

  const handleField = (field) => (event) => {
    setProfile((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const submitSection = async (sectionFields, setSaving, successMessage) => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        sectionFields.map((field) => [field, profile[field] ?? null])
      );
      const updated = await updateCurrentUser(payload);
      setUser && setUser(updated);
      showPopup(successMessage, 'success');
    } catch {
      showPopup('Failed to save changes', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleContactSave = (e) => {
    e.preventDefault();
    submitSection(['full_name', 'email', 'phone'], setSavingContact, 'Contact info updated');
  };

  const handleAddressSave = (e) => {
    e.preventDefault();
    submitSection(
      ['address_line1', 'address_line2', 'city', 'state', 'postal_code', 'country'],
      setSavingAddress,
      'Address updated'
    );
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (!oldPassword || !newPassword || !confirmPassword) {
      showPopup('All fields are required.', 'error');
      return;
    }
    if (newPassword !== confirmPassword) {
      showPopup('New passwords do not match.', 'error');
      return;
    }
    if (!isValidPassword(newPassword)) {
      showPopup('Password does not meet requirements.', 'error');
      return;
    }
    setResetting(true);
    try {
      await resetPassword({ oldPassword, newPassword });
      showPopup('Password reset successfully', 'success');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowReset(false);
    } catch (err) {
      showPopup(err?.response?.data?.message || 'Failed to reset password', 'error');
    } finally {
      setResetting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      <PageHeader
        eyebrow="Profile"
        title="Account"
        description="Update your contact information and password."
        actions={
          <>
            <Button variant="secondary" iconLeft={<FaKey />} onClick={() => setShowReset(true)}>
              Change password
            </Button>
            <Button variant="ghost" iconLeft={<FaSignOutAlt />} onClick={handleLogout}>
              Sign out
            </Button>
          </>
        }
      />

      <div className={styles.page}>
        <Card className={styles.identityCard}>
          <div className={styles.identityRow}>
            <div className={styles.avatar} aria-hidden="true">{initials}</div>
            <div className={styles.identityText}>
              <h2 className={styles.identityName}>{displayName}</h2>
              <p className={styles.identityEmail}>{user?.email || '—'}</p>
              <div className={styles.identityBadges}>
                <Badge variant={role === 'Admin' ? 'brand' : 'neutral'} icon={<FaShieldAlt />}>{role}</Badge>
                <Badge variant={user?.is_active ? 'success' : 'danger'}>
                  {user?.is_active ? 'Active' : 'Inactive'}
                </Badge>
                {user?.last_login_at ? (
                  <Badge variant="info" icon={<FaClock />}>Last login {formatDateTime(user.last_login_at)}</Badge>
                ) : null}
              </div>
            </div>
          </div>
        </Card>

        <div className={styles.grid}>
          <Card>
            <CardHeader title="Contact" subtitle="How we reach you." />
            <CardBody>
              <form onSubmit={handleContactSave} className={styles.form}>
                <Input
                  label="Full name"
                  value={profile.full_name || ''}
                  onChange={handleField('full_name')}
                  iconLeft={<FaUser />}
                  required
                />
                <Input
                  label="Email"
                  type="email"
                  value={profile.email || ''}
                  onChange={handleField('email')}
                  iconLeft={<FaEnvelope />}
                  required
                />
                <Input
                  label="Phone"
                  value={profile.phone || ''}
                  onChange={handleField('phone')}
                  iconLeft={<FaPhone />}
                  hint="Optional. Used for account recovery only."
                />
                <div className={styles.actions}>
                  <Button type="submit" loading={savingContact} iconLeft={<FaCheck />}>Save</Button>
                </div>
              </form>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Address" subtitle="Mailing or service address." />
            <CardBody>
              <form onSubmit={handleAddressSave} className={styles.form}>
                <Input
                  label="Street"
                  value={profile.address_line1 || ''}
                  onChange={handleField('address_line1')}
                  iconLeft={<FaMapMarkerAlt />}
                  placeholder="123 Main St"
                />
                <Input
                  label="Apt / suite (optional)"
                  value={profile.address_line2 || ''}
                  onChange={handleField('address_line2')}
                  placeholder="Apt 4B"
                />
                <div className={styles.row2}>
                  <Input
                    label="City"
                    value={profile.city || ''}
                    onChange={handleField('city')}
                  />
                  <Input
                    label="State / Region"
                    value={profile.state || ''}
                    onChange={handleField('state')}
                  />
                </div>
                <div className={styles.row2}>
                  <Input
                    label="Postal code"
                    value={profile.postal_code || ''}
                    onChange={handleField('postal_code')}
                  />
                  <Input
                    label="Country"
                    value={profile.country || ''}
                    onChange={handleField('country')}
                  />
                </div>
                <div className={styles.actions}>
                  <Button type="submit" loading={savingAddress} iconLeft={<FaCheck />}>Save</Button>
                </div>
              </form>
            </CardBody>
          </Card>

          <Card className={styles.fullSpan}>
            <CardHeader
              title="Security"
              subtitle="Keep your account secure."
              actions={
                <Button variant="secondary" iconLeft={<FaKey />} onClick={() => setShowReset(true)}>
                  Change password
                </Button>
              }
            />
            <CardBody>
              <div className={styles.securityRow}>
                <div>
                  <span className={styles.label}>Last sign-in</span>
                  <p className={styles.muted}>{formatDateTime(user?.last_login_at)}</p>
                </div>
                <div>
                  <span className={styles.label}>Account created</span>
                  <p className={styles.muted}>{formatDateTime(user?.created_at)}</p>
                </div>
                <div>
                  <span className={styles.label}>Last updated</span>
                  <p className={styles.muted}>{formatDateTime(user?.modified_at)}</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>

      <Modal
        open={showReset}
        onClose={() => setShowReset(false)}
        title="Change password"
        description="Enter your current password and choose a new strong one."
        size="sm"
      >
        <form className={styles.form} onSubmit={handleReset}>
          <Input
            label="Current password"
            type="password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            required
            autoFocus
          />
          <Input
            label="New password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          <Input
            label="Confirm new password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          <ul className={styles.requirements}>
            {PASSWORD_RULES.map((rule) => (
              <li key={rule}>{rule}</li>
            ))}
          </ul>
          <div className={styles.actions}>
            <Button variant="secondary" type="button" onClick={() => setShowReset(false)}>Cancel</Button>
            <Button type="submit" loading={resetting} iconLeft={<FaKey />}>Update password</Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
